import * as React from "react"
import { useTranslation } from "react-i18next"
import { Button, DialogDescription, DialogHeader, DialogTitle } from "@nexus/ui"

export interface SendSuccessViewProps {
  vendorName: string
  count: number
  autoCloseMs: number
  onDone: () => void
}

export function SendSuccessView({ vendorName, count, autoCloseMs, onDone }: SendSuccessViewProps) {
  const { t } = useTranslation()
  const doneRef = React.useRef(false)

  const finish = React.useCallback(() => {
    if (doneRef.current) return
    doneRef.current = true
    onDone()
  }, [onDone])

  React.useEffect(() => {
    const timer = window.setTimeout(finish, autoCloseMs)
    return () => window.clearTimeout(timer)
  }, [finish, autoCloseMs])

  return (
    <div className="relative flex flex-col items-center px-2 pt-2 pb-1 text-center">
      <div className="relative mb-4 flex h-16 w-16 items-center justify-center">
        <span className="stv-badge flex h-16 w-16 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--success)_14%,transparent)]">
          <svg
            className="stv-check h-8 w-8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--success)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
      </div>

      <DialogHeader className="items-center gap-1.5">
        <DialogTitle className="stv-rise text-base font-medium" style={{ animationDelay: "120ms" }}>
          {t("opportunities.scripts.sendDialog.successTitle")}
        </DialogTitle>
        <DialogDescription className="stv-rise text-sm" style={{ animationDelay: "180ms" }}>
          {t(
            count === 1
              ? "opportunities.scripts.sendDialog.successBody_one"
              : "opportunities.scripts.sendDialog.successBody_other",
            { count, vendorName },
          )}
        </DialogDescription>
      </DialogHeader>

      <Button
        size="sm"
        variant="outline"
        className="stv-rise mt-5"
        style={{ animationDelay: "240ms" }}
        onClick={finish}
      >
        {t("opportunities.scripts.sendDialog.successDone")}
      </Button>

      <div className="mt-5 h-0.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="stv-countdown-bar h-full rounded-full bg-success"
          style={{ "--stv-countdown-ms": `${autoCloseMs}ms` } as React.CSSProperties}
        />
      </div>
    </div>
  )
}
